import React, { useEffect } from 'react';
import { Card, Grid } from '@material-ui/core';
import { Book as BookIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineRadioGroup, {
  InlineRadioGroupProps
} from '../../../common/inline/InlineRadioGroup';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import { BaseSupplierCardProps } from '../Supplier.interface';
import { StyledDivider, StyledTypography } from './SupplierCard.styled';

const SupplierQualityManualCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Supplier>(
    supplier.task,
    { token }
  );
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;
  const commonOptions: InlineRadioGroupProps['options'] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'N/A', value: 'n/a' }
  ];
  const commonGridProps: InlineRadioGroupProps['grid'] = {
    xs: 12
  };

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <BookIcon />
        Quality Manual
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          isSubmitting={isUpdating}
          initialValues={{
            hasOrganizationChart: initialData.hasOrganizationChart || '',
            hasTechnicalDataControl: initialData.hasTechnicalDataControl || '',
            hasDocumentRetentionControl:
              initialData.hasDocumentRetentionControl || '',
            hasTrainingLogs: initialData.hasTrainingLogs || '',
            hasShelfLifeProgram: initialData.hasShelfLifeProgram || '',
            hasQualityControl: initialData.hasQualityControl || '',
            hasReceivingInspectionProcedures:
              initialData.hasReceivingInspectionProcedures || '',
            hasCalibrationProgram: initialData.hasCalibrationProgram || '',
            hasRevisions: initialData.hasRevisions || '',
            qualityReviewProcedure: initialData.qualityReviewProcedure || '',
            isQualityManualAvailable: initialData.isQualityManualAvailable || ''
          }}
          onSubmit={(supplierDto: Partial<any>) => {
            updateSupplier(supplierDto);
            onChange?.(supplierDto);
          }}
        >
          {() => (
            <InlineForm>
              <Grid item xs={12}>
                <StyledTypography variant='h5'>
                  Does the Quality Manual Include the following:
                </StyledTypography>
              </Grid>
              <InlineRadioGroup
                name='hasOrganizationChart'
                label='An Organization Chart?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasOrganizationChart')}
              />
              <InlineRadioGroup
                name='hasTechnicalDataControl'
                label='An accurate description of the technical data distribution and revision control system?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasTechnicalDataControl')
                }
              />
              <InlineRadioGroup
                name='hasDocumentRetentionControl'
                label='A record keeping system and retention times for all documents?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('hasDocumentRetentionControl')
                }
              />
              <InlineRadioGroup
                name='hasTrainingLogs'
                label='Personnel training requirements and records?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasTrainingLogs')}
              />
              <InlineRadioGroup
                name='hasShelfLifeProgram'
                label='Details of the shelf life program?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasShelfLifeProgram')}
              />
              <InlineRadioGroup
                name='hasQualityControl'
                label='Defines the control of rejected and/or scrapped parts?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasQualityControl')}
              />
              <InlineRadioGroup
                name='hasReceivingInspectionProcedures'
                label='An outline of the receiving inspection procedures?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled ||
                  isFieldDisabled('hasReceivingInspectionProcedures')
                }
              />
              <InlineRadioGroup
                name='hasCalibrationProgram'
                label='An outline of the tool and gauge calibration program?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasCalibrationProgram')}
              />
              <InlineRadioGroup
                name='hasRevisions'
                label='A revision page which reflects revisions?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={disabled || isFieldDisabled('hasRevisions')}
              />
              <Grid item xs={12}>
                <StyledDivider />
              </Grid>
              <InlineTextField
                name='qualityReviewProcedure'
                label='How often Is the Quality Manual reviewed and by whom?'
                placeholder='Enter answer'
                disabled={disabled || isFieldDisabled('qualityReviewProcedure')}
              />
              <InlineRadioGroup
                name='isQualityManualAvailable'
                label='Is the Quality Manual readily available to employees?'
                grid={commonGridProps}
                options={commonOptions}
                disabled={
                  disabled || isFieldDisabled('isQualityManualAvailable')
                }
              />
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierQualityManualCard;
